import { MouseEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { getBorderClassNames } from '@nx-frontend/shared/themes';
import { PortalBorderType, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { $isDeletingAiThreadId } from './store';

dayjs.extend(relativeTime);

interface ChatbotThreadComponentProps {
  id: string;
  title?: string;
  date: number;
  onClick: (id: string) => void;
  onDelete: (id: string) => void;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
}

export function ChatbotThreadComponent({
  id,
  title,
  date,
  onClick,
  onDelete,
  themeColors,
  themeStyle,
}: ChatbotThreadComponentProps) {
  const isDeletingAiThreadId = useUnit($isDeletingAiThreadId);

  const t = useTranslations('features');

  // new backend returns date in milliseconds, so we convert seconds to ms
  // compare with 2030 year date
  if (date < 1893456000) {
    // it is seconds
    date *= 1000;
  }

  const onThreadClick = useCallback(() => {
    onClick(id);
  }, [id, onClick]);

  const onThreadDelete = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      onDelete(id);
    },
    [id, onDelete]
  );

  return (
    <div
      data-cy={`chatbot-thread-${id}`}
      className={classNames(
        'group/thread flex items-center rounded-lg py-[9px] px-3 cursor-pointer transition-colors duration-200 hover:bg-neutral-100 justify-between',
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
      )}
      onClick={onThreadClick}
    >
      <span
        className={classNames('block whitespace-nowrap text-sm', {
          'overflow-hidden text-ellipsis': Boolean(title),
        })}
        title={title || t('ai-helper.untitled')}
      >
        {title || <em>{t('ai-helper.untitled')}</em>}
      </span>
      {isDeletingAiThreadId !== id ? (
        <>
          <button
            className="hidden cursor-pointer w-5 h-5 group-hover/thread:flex items-center transition-colors duration-200 justify-center hover:bg-neutral-200 rounded"
            onClick={onThreadDelete}
          >
            <Icon icon={IconsMap.TRASH} className="w-4 text-neutral-500" />
          </button>
          <span className="text-xs text-neutral-500 group-hover/thread:hidden whitespace-nowrap ml-2">
            {dayjs(date).isAfter(dayjs().startOf('day').subtract(2, 'day'))
              ? dayjs(date).fromNow()
              : dayjs(date).format('DD/MM/YYYY')}
          </span>
        </>
      ) : (
        <Spinner width={20} height={20} />
      )}
    </div>
  );
}
